// src/fleet-stats/analytics/detectAnomalies.js
import { ANOMALY_DROP_THRESHOLD, ANOMALY_SPIKE_THRESHOLD, BASELINE_WINDOW_DAYS, RECENT_WINDOW_DAYS } from "../constants.js";
import { addDays, detectOperatesWeekends, detectSiteOperatesWeekends, smartWeekdayFilter } from "../utils/date.js";

/**
 * For each robot, compare recent RECENT_WINDOW_DAYS vs baseline BASELINE_WINDOW_DAYS.
 * Weekend handling: detected per robot from its own uptime data, with site-level fallback.
 *
 * Returns anomalies: { robotId, robotName, client, siteName, type, metric, recentAvg, baselineAvg, changePct }
 * type: "drop" | "spike"
 * metric: "km" | "hours"
 */
export function detectAnomalies(rows, maxDate) {
    if (!maxDate || rows.length === 0) return [];

    const recentFrom   = addDays(maxDate, -(RECENT_WINDOW_DAYS - 1));
    const baselineFrom = addDays(maxDate, -(BASELINE_WINDOW_DAYS + RECENT_WINDOW_DAYS - 1));
    const baselineTo   = addDays(maxDate, -RECENT_WINDOW_DAYS);

    // ── Site-level weekend patterns ──────────────────────────────────────────
    const bySiteAll = new Map();
    for (const r of rows) {
        if (!bySiteAll.has(r.siteName)) bySiteAll.set(r.siteName, []);
        bySiteAll.get(r.siteName).push(r);
    }
    const siteWeekendPattern = new Map();
    for (const [siteName, siteRows] of bySiteAll.entries()) {
        siteWeekendPattern.set(siteName, detectSiteOperatesWeekends(siteRows));
    }

    // ── Group by robot ───────────────────────────────────────────────────────
    const byRobot = new Map();
    for (const r of rows) {
        if (!byRobot.has(r.robotId)) {
            byRobot.set(r.robotId, {
                robotId: r.robotId, robotName: r.robotName,
                client: r.client, siteName: r.siteName,
                allDays: [], recent: [], baseline: [],
            });
        }
        const entry = byRobot.get(r.robotId);
        entry.allDays.push(r);
        if (r.date >= recentFrom && r.date <= maxDate) entry.recent.push(r);
        if (r.date >= baselineFrom && r.date <= baselineTo) entry.baseline.push(r);
    }

    const anomalies = [];

    for (const entry of byRobot.values()) {
        if (entry.baseline.length < 5) continue;

        const robotWknd = detectOperatesWeekends(entry.allDays);
        const siteWknd  = siteWeekendPattern.get(entry.siteName) ?? null;

        const recentFiltered   = smartWeekdayFilter(entry.recent,   robotWknd, siteWknd);
        const baselineFiltered = smartWeekdayFilter(entry.baseline, robotWknd, siteWknd);

        const avg = (arr, fn) =>
            arr.length === 0 ? 0 : arr.reduce((s, r) => s + fn(r), 0) / arr.length;

        const metrics = [
            {
                key: "km",
                recentAvg:   avg(recentFiltered,   (r) => r.distance_m / 1000),
                baselineAvg: avg(baselineFiltered, (r) => r.distance_m / 1000),
            },
            {
                key: "hours",
                recentAvg:   avg(recentFiltered,   (r) => r.moving_time_s / 3600),
                baselineAvg: avg(baselineFiltered, (r) => r.moving_time_s / 3600),
            },
        ];

        for (const m of metrics) {
            if (m.baselineAvg < 0.05) continue;

            const ratio     = m.recentAvg / m.baselineAvg;
            const changePct = (m.recentAvg - m.baselineAvg) / m.baselineAvg;

            if (ratio < ANOMALY_DROP_THRESHOLD) {
                anomalies.push({
                    robotId: entry.robotId, robotName: entry.robotName,
                    client: entry.client, siteName: entry.siteName,
                    type: "drop", metric: m.key,
                    recentAvg: m.recentAvg, baselineAvg: m.baselineAvg, changePct,
                });
            } else if (ratio > ANOMALY_SPIKE_THRESHOLD) {
                anomalies.push({
                    robotId: entry.robotId, robotName: entry.robotName,
                    client: entry.client, siteName: entry.siteName,
                    type: "spike", metric: m.key,
                    recentAvg: m.recentAvg, baselineAvg: m.baselineAvg, changePct,
                });
            }
        }
    }

    return anomalies.sort((a, b) => {
        if (a.type !== b.type) return a.type === "drop" ? -1 : 1;
        return Math.abs(b.changePct) - Math.abs(a.changePct);
    });
}

/**
 * Find dates in a daily series where value deviates significantly from 7d rolling avg.
 * Used to place markers on the activity chart.
 * Note: chart markers operate on aggregated fleet data so per-robot weekend
 * detection doesn't apply — the 7d rolling average already smooths this out.
 */
export function detectChartMarkers(series, metric = "km") {
    const markers = [];

    for (let i = 7; i < series.length; i++) {
        const point    = series[i];
        const baseline = metric === "km" ? point.km_7d : point.hours_7d;
        const value    = point[metric];

        if (baseline < 0.1) continue;

        const ratio = value / baseline;
        if (ratio < 0.3) {
            markers.push({ date: point.date, type: "drop",  value, baseline, metric });
        } else if (ratio > 3.0) {
            markers.push({ date: point.date, type: "spike", value, baseline, metric });
        }
    }

    return markers;
}